(function attachOrienXHero(globalScope, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (globalScope) {
    globalScope.OrienXHero = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function createOrienXHeroApi() {
  function createCarouselController(options) {
    const root = options.root;
    const duration = Number(options.duration) || 6000;
    const reducedMotion = Boolean(options.reducedMotion);
    const onRender = typeof options.onRender === 'function' ? options.onRender : function noop() {};
    const scheduler = options.scheduler || {
      setTimeout: globalThis.setTimeout.bind(globalThis),
      clearTimeout: globalThis.clearTimeout.bind(globalThis),
    };
    const slides = Array.from(root.querySelectorAll('[data-ox-hero-slide]'));
    const controls = Array.from(root.querySelectorAll('[data-ox-hero-control]'));
    const productLinks = Array.from(root.querySelectorAll('[data-ox-hero-product-link]'));
    let activeIndex = 0;
    let timerId = null;
    let paused = false;
    let destroyed = false;

    function render() {
      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });

      controls.forEach((control, index) => {
        const isActive = index === activeIndex;
        control.classList.toggle('is-active', isActive);
        control.setAttribute('aria-pressed', String(isActive));
      });

      productLinks.forEach((link, index) => {
        link.setAttribute('tabindex', index === activeIndex ? '0' : '-1');
      });

      onRender(activeIndex);
    }

    function stopTimer() {
      if (timerId === null) return;
      scheduler.clearTimeout(timerId);
      timerId = null;
    }

    function startTimer() {
      stopTimer();
      if (destroyed || paused || reducedMotion || slides.length < 2) return;
      timerId = scheduler.setTimeout(() => {
        timerId = null;
        goTo(activeIndex + 1, false);
      }, duration);
    }

    function goTo(index) {
      if (!slides.length || destroyed) return;
      activeIndex = ((Number(index) % slides.length) + slides.length) % slides.length;
      stopTimer();
      render();
      startTimer();
    }

    function pause() {
      paused = true;
      root.classList && root.classList.add('is-paused');
      stopTimer();
    }

    function resume() {
      if (destroyed) return;
      paused = false;
      root.classList && root.classList.remove('is-paused');
      render();
      startTimer();
    }

    function destroy() {
      destroyed = true;
      root.classList && root.classList.remove('is-paused');
      stopTimer();
    }

    render();
    startTimer();

    return { goTo, pause, resume, destroy };
  }

  function init(root, options) {
    options = options || {};
    if (!root || root.dataset.oxHeroReady === 'true') {
      return root && root.oxHeroInstance;
    }

    root.dataset.oxHeroReady = 'true';
    const hostWindow = options.window || (typeof window !== 'undefined' ? window : globalThis);
    const hostDocument = options.document || root.ownerDocument || hostWindow.document;
    const reducedMotion = options.reducedMotion !== undefined
      ? Boolean(options.reducedMotion)
      : Boolean(hostWindow.matchMedia && hostWindow.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const controls = Array.from(root.querySelectorAll('[data-ox-hero-control]'));
    const cleanup = [];

    function listen(target, type, listener) {
      if (!target || !target.addEventListener) return;
      target.addEventListener(type, listener);
      cleanup.push(() => target.removeEventListener(type, listener));
    }

    const controller = createCarouselController({
      root,
      duration: options.duration || Number(root.dataset.oxHeroDuration) || 6000,
      scheduler: options.scheduler,
      reducedMotion,
      onRender(index) {
        controls.forEach((control) => control.classList.remove('is-progressing'));
        const activeControl = controls[index];
        if (!activeControl || reducedMotion) return;
        void activeControl.offsetWidth;
        activeControl.classList.add('is-progressing');
      },
    });

    controls.forEach((control, index) => {
      listen(control, 'click', () => controller.goTo(index, true));
    });
    listen(root, 'focusin', controller.pause);
    listen(root, 'focusout', (event) => {
      if (!root.contains(event.relatedTarget)) controller.resume();
    });
    listen(hostDocument, 'visibilitychange', () => {
      hostDocument.hidden ? controller.pause() : controller.resume();
    });

    const instance = {
      goTo: controller.goTo,
      pause: controller.pause,
      resume: controller.resume,
      destroy() {
        cleanup.splice(0).forEach((removeListener) => removeListener());
        controller.destroy();
        root.dataset.oxHeroReady = 'false';
        root.oxHeroInstance = null;
      },
    };
    root.oxHeroInstance = instance;
    return instance;
  }

  function boot(scope) {
    const base = scope || document;
    if (base.matches && base.matches('[data-ox-product-hero]')) init(base);
    if (base.querySelectorAll) base.querySelectorAll('[data-ox-product-hero]').forEach((root) => init(root));
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
    } else {
      boot(document);
    }
    document.addEventListener('shopify:section:load', (event) => boot(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      const root = event.target.matches && event.target.matches('[data-ox-product-hero]')
        ? event.target
        : event.target.querySelector && event.target.querySelector('[data-ox-product-hero]');
      if (root && root.oxHeroInstance) root.oxHeroInstance.destroy();
    });
  }

  return { createCarouselController, init };
});
